import { useMemo } from 'react';
import {
    PosMicrosIntegrationIntegrationConfiguration,
    PosMicrosIntegrationMicros4700Config,
    PosMicrosIntegrationTransmissionProtocol,
    PosMicrosIntegrationXmlPosConfig,
} from '@mews-commander/data-access';
import { createZodFormResolver, getDefaults, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import {
    microsConfigurationFormSchema,
    MicrosConfigurationFormSchema,
    xmlPosConfigurationFormSchema,
    XmlPosConfigurationFormSchema,
} from '../components/formProps';

interface UsePosMicrosIntegrationConfigProps {
    data:
        | (PosMicrosIntegrationIntegrationConfiguration & {
              configuration?: PosMicrosIntegrationMicros4700Config | null;
              xmlPosConfiguration?: PosMicrosIntegrationXmlPosConfig | null;
          })
        | undefined;
}

export const usePosMicrosIntegrationConfig = ({ data }: UsePosMicrosIntegrationConfigProps) => {
    const posIntegrationsServiceKeyEnabled = useFeatureFlag(FeatureFlag.posIntegrationsServiceKeyEnabled);

    const integrationName = data?.integrationName?.toLowerCase();
    const microsConfiguration = data?.configuration;
    const xmlPosConfiguration = data?.xmlPosConfiguration;

    const isXmlPosIntegration = useMemo(
        () =>
            integrationName?.includes(PosMicrosIntegrationTransmissionProtocol.XmlPos.toLowerCase()) &&
            posIntegrationsServiceKeyEnabled,
        [integrationName, posIntegrationsServiceKeyEnabled]
    );

    const isMicrosIPIntegration = useMemo(
        () => integrationName?.includes(PosMicrosIntegrationTransmissionProtocol.MicrosIP.toLowerCase()),
        [integrationName]
    );

    const formFormat = useMemo(
        () =>
            isXmlPosIntegration
                ? PosMicrosIntegrationTransmissionProtocol.XmlPos
                : PosMicrosIntegrationTransmissionProtocol.MicrosIP,
        [isXmlPosIntegration]
    );

    const posConfiguration = useMemo(
        () => (isXmlPosIntegration && xmlPosConfiguration ? xmlPosConfiguration : microsConfiguration),
        [isXmlPosIntegration, xmlPosConfiguration, microsConfiguration]
    );

    const resolver = useMemo(
        () =>
            isXmlPosIntegration
                ? createZodFormResolver<XmlPosConfigurationFormSchema>(xmlPosConfigurationFormSchema)
                : createZodFormResolver<MicrosConfigurationFormSchema>(microsConfigurationFormSchema),
        [isXmlPosIntegration]
    );

    const defaultValues = useMemo(() => {
        const microsConfigurationFormDefaultValues: PosMicrosIntegrationMicros4700Config =
            getDefaults(microsConfigurationFormSchema);
        const xmlConfigurationFormDefaultValues: PosMicrosIntegrationXmlPosConfig =
            getDefaults(xmlPosConfigurationFormSchema);

        return isXmlPosIntegration ? xmlConfigurationFormDefaultValues : microsConfigurationFormDefaultValues;
    }, [isXmlPosIntegration]);

    const protocol = useMemo(
        () =>
            microsConfiguration?.protocol ||
            xmlPosConfiguration?.protocol ||
            PosMicrosIntegrationTransmissionProtocol.MicrosIP,
        [microsConfiguration?.protocol, xmlPosConfiguration?.protocol]
    );

    const microsOutlets = useMemo(() => {
        if (isXmlPosIntegration) {
            return xmlPosConfiguration ? xmlPosConfiguration.microsOutlets ?? [] : [];
        }
        return microsConfiguration ? microsConfiguration.microsOutlets ?? [] : [];
    }, [isXmlPosIntegration, xmlPosConfiguration, microsConfiguration]);

    return {
        isXmlPosIntegration,
        isMicrosIPIntegration,
        formFormat,
        posConfiguration,
        resolver,
        defaultValues,
        protocol,
        microsOutlets,
    };
};
